/**
 * DeploymentChecklistPage — /portfolio-kit/deployment-checklist
 *
 * Printable / copy-able deployment & observability checklist.
 * Organized by category with must-have / should-have / nice-to-have gates.
 * All Access content (some items are must-haves for free users too).
 */

import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockIcon from "@mui/icons-material/Lock";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DEPLOYMENT_CHECKLIST } from "../data/portfolioKit";
import { L } from "../utils/localized";
import { useAuth } from "../hooks/useAuth";
import { getTierAccess } from "../utils/tier";
import ProUpsellNote from "../components/ProUpsellNote";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

export default function DeploymentChecklistPage() {
  const theme = useTheme();
  const { t, i18n } = useTranslation("pages-core");
  const checklist = L(DEPLOYMENT_CHECKLIST, i18n.language);

  const CATEGORY_LABELS: Record<string, { label: string; color: string; description: string }> = {
    deployment: {
      label: t("DeploymentChecklistPage.categoryDeploymentLabel"),
      color: theme.palette.primary.main,
      description: t("DeploymentChecklistPage.categoryDeploymentDescription"),
    },
    observability: {
      label: t("DeploymentChecklistPage.categoryObservabilityLabel"),
      color: theme.palette.info.main, // genuine blue (#2563eb) — intentional informational color
      description: t("DeploymentChecklistPage.categoryObservabilityDescription"),
    },
    safety: {
      label: t("DeploymentChecklistPage.categorySafetyLabel"),
      color: theme.palette.error.main,
      description: t("DeploymentChecklistPage.categorySafetyDescription"),
    },
    cost: {
      label: t("DeploymentChecklistPage.categoryCostLabel"),
      color: theme.palette.warning.main,
      description: t("DeploymentChecklistPage.categoryCostDescription"),
    },
    documentation: {
      label: t("DeploymentChecklistPage.categoryDocumentationLabel"),
      color: theme.palette.primary.main,
      description: t("DeploymentChecklistPage.categoryDocumentationDescription"),
    },
  };

  const GATE_STYLES: Record<string, { color: string; label: string }> = {
    "must-have": { color: theme.palette.error.main, label: t("DeploymentChecklistPage.gateMustHaveLabel") },
    "should-have": { color: theme.palette.warning.main, label: t("DeploymentChecklistPage.gateShouldHaveLabel") },
    "nice-to-have": { color: theme.palette.text.secondary, label: t("DeploymentChecklistPage.gateNiceToHaveLabel") },
  };
  const navigate = useNavigate();
  const { user } = useAuth();
  const tierAccess = getTierAccess(user);
  const isAllAccess = tierAccess.canAccessProducts || tierAccess.isModerator;

  // Group by category
  const categories = Array.from(new Set(checklist.map((i) => i.category)));

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 5, md: 7 },
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/portfolio-kit")}
            sx={{ color: theme.palette.primary.main, mb: 2 }}
          >
            {t("DeploymentChecklistPage.backToPortfolioKitButton")}
          </Button>
          <Chip
            label={t("DeploymentChecklistPage.heroChipLabel")}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.72rem",
              letterSpacing: "0.08em",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              mb: 2,
            }}
          />
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em", mb: 1.5 }}>
            {t("DeploymentChecklistPage.heroTitle")}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 820, lineHeight: 1.5 }}>
            {t("DeploymentChecklistPage.heroSubtitle")}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {!isAllAccess && tierAccess.isPro && <ProUpsellNote />}
        <Stack spacing={5}>
          {categories.map((cat) => {
            const items = checklist.filter((i) => i.category === cat);
            const meta = CATEGORY_LABELS[cat];
            return (
              <Box key={cat}>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                  <Box sx={{ width: 6, height: 24, bgcolor: meta.color, borderRadius: 1 }} />
                  <Typography variant="h5" fontWeight={500} sx={{ letterSpacing: "-0.01em" }}>
                    {meta.label}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" mb={2} sx={{ maxWidth: 720 }}>
                  {meta.description}
                </Typography>
                <Stack spacing={1.5}>
                  {items.map((item) => {
                    const gate = GATE_STYLES[item.gate];
                    // "nice-to-have" items are shown only to all_access
                    const isGated = !isAllAccess && item.gate === "nice-to-have";
                    return (
                      <Paper
                        key={item.id}
                        sx={{
                          p: 2.5,
                          border: `1px solid ${alpha(gate.color, 0.25)}`,
                          borderLeft: `4px solid ${gate.color}`,
                          opacity: isGated ? 0.6 : 1,
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          {isGated ? (
                            <LockIcon sx={{ color: theme.palette.primary.main, mt: 0.3 }} />
                          ) : (
                            <CheckCircleOutlineIcon sx={{ color: gate.color, mt: 0.3 }} />
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={1}>
                              <Typography variant="body1" fontWeight={500} sx={{ flex: 1, minWidth: 0 }}>
                                {item.item}
                              </Typography>
                              <Chip
                                label={gate.label}
                                size="small"
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "0.65rem",
                                  bgcolor: alpha(gate.color, 0.1),
                                  color: gate.color,
                                }}
                              />
                            </Stack>
                            {!isGated ? (
                              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                {item.rationale}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                {t("DeploymentChecklistPage.lockedRationaleMessage")}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
        </Stack>

        {/* Footer CTA */}
        <Paper sx={{ mt: 5, p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, textAlign: "center" }}>
          <Typography variant="h6" fontWeight={500} mb={1}>
            {t("DeploymentChecklistPage.footerCtaTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t("DeploymentChecklistPage.footerCtaBody")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
            <Button
              variant="contained"
              onClick={() => navigate("/portfolio-kit/lovable-prompt")}
              sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}
            >
              {t("DeploymentChecklistPage.openLovablePromptKitButton")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/portfolio-kit")}
              sx={{ borderColor: alpha(theme.palette.primary.main, 0.5), color: theme.palette.primary.main }}
            >
              {t("DeploymentChecklistPage.backToPortfolioKitButton")}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
